import {ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy} from '@angular/core';
import {A0, Module, ModuleTitleItem, ModuleType, Snapshot, SnapshotType} from '../../../../shared/entities/A0.entity';
import {MapperService} from '../../../../services/mapper.service';
import {ENTITY_Z_ORDER, getContainerDimension} from '../../../../shared/tools';
import {ModuleCustomImgComponent} from './module-custom-img.component';
import {SharedModule} from '../../../../shared/shared.module';

@Component({
  selector: 'modules-dynamic-position',
  template: `
    <div class="blockParent dragBoundary">
      @if (containerDimension.width > 0) {

        @for (module of listModules; track module.id) {
          @switch (module.id_module) {
            @case (ModuleType.MODULE_CUSTOM) {
              <dynamicPosition [container]="containerDimension"
                               (positionChange)="onModulePositionChange($event, module)"
                               [zIndex]="ENTITY_Z_ORDER.MODULE_CUSTOM"
                               [position]="module.position"
                               innerClass="module-custom-area highlight-custom-{{module.id}}">
                <custom-img [entity]="module" [containerDimension]="containerDimension"/>
              </dynamicPosition>
            }
            @case (ModuleType.MODULE_LEGENDE_STATIC) {
              <dynamicPosition [container]="containerDimension"
                               (positionChange)="onModulePositionChange($event, module)"
                               [position]="module.position"
                               [zIndex]="ENTITY_Z_ORDER.LEGEND"
                               innerClass="reserved-area reserved-area-1">
                <div class="p-2">
                  <div class="plan-preview-tag">7</div>
                  <div class="plan-preview-name">Légende</div>
                </div>
              </dynamicPosition>
            }
            @case (ModuleType.MODULE_STRATEGIE_ALERTE_COMMUNALE) {
              <dynamicPosition [container]="containerDimension"
                               (positionChange)="onModulePositionChange($event, module)"
                               [position]="module.position"
                               [zIndex]="ENTITY_Z_ORDER.LEGEND"
                               innerClass="reserved-area reserved-area-2">
                <div class="p-2">
                  <div class="plan-preview-tag">8</div>
                  <div class="plan-preview-name">Alerte communale</div>
                </div>
              </dynamicPosition>
            }
          }
        }

        @for (zoom of listZoom; track zoom.id_zoom) {
          <dynamicPosition [container]="containerDimension"
                           (positionChange)="onZoomPositionChange($event, zoom)"
                           [position]="zoom.position"
                           [zIndex]="ENTITY_Z_ORDER.ZOOM"
                           innerClass="zoom-area zoom-area-2 highlight-zoom-{{zoom.id_zoom}} {{ENTITY_Z_ORDER.ZOOM}}">
            <img class="img-fluid" [src]="zoom.snapshot_url_image + '?v=' + zoom.snapshot_date" [alt]="zoom.id_zoom">
          </dynamicPosition>
        }

        @for (title of listTitles; track title.id) {
          @let titleFontSize = ((title.fontSize * 7.80) * printScale);
          <dynamicPosition [container]="containerDimension"
                           (positionChange)="onTitlePositionChange($event, title)"
                           [zIndex]="ENTITY_Z_ORDER.TITLE"
                           [position]="title.position"
                           innerClass="title-area highlight-title-{{title.id}}">
            <span [ngStyle]="{
                'color': title.color,
                'line-height':  1.3,
                'font-size': titleFontSize + 'pt',
                'text-shadow': title.shadow ? '1px 1px 1px #000000' : '',
            }" class="title-text">
              {{title.name}}
            </span>
          </dynamicPosition>
        }
      }

      <ng-content/>
    </div>
  `,
  imports: [
    ModuleCustomImgComponent,
    SharedModule
  ],
  styles: [`
    .title-text {
      font-family: Arial, sans-serif !important;
    }
  `]
})
export class ModulesDynamicPosition implements OnChanges, OnDestroy {

  protected readonly ModuleType = ModuleType;
  protected readonly ENTITY_Z_ORDER = ENTITY_Z_ORDER;

  @Input() plan: A0;
  @Input() listModules: Module[];
  @Input() listTitles: ModuleTitleItem[];
  @Input() listZoom: Snapshot[];
  @Input() map: any;

  containerDimension = {
    width: 0,
    height: 0
  };

  get printScale(): number {
    return this.containerDimension.width
      ? this.containerDimension.width / 6785
      : 0;
  }

  private resizeObserver?: ResizeObserver;
  private observedContainer?: Element;

  constructor(
    public mapperService: MapperService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    if (this.map?.viewer) {
      this.updateContainerDimension();
      this.observeMapContainer();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private updateContainerDimension(): void {
    this.containerDimension = getContainerDimension(this.map.viewer);
    this.cdr.markForCheck();
  }

  private observeMapContainer(): void {
    const container: Element | undefined = this.map?.viewer?.container;
    if (!container || container === this.observedContainer) {
      return;
    }

    this.resizeObserver?.disconnect();
    this.observedContainer = container;
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.updateContainerDimension());
    });
    this.resizeObserver.observe(container);
  }

  onModulePositionChange(position: {x: number; y: number}, item: Module): void {
    const _position = position.x + ',' + position.y;
    this.mapperService.createOrUpdateModule(this.plan, {...item,
      type: item.id_module as ModuleType,
      position: _position
    }).subscribe(() => {
      const moduleIdx = this.listModules.findIndex(i => i.id === item.id);
      this.listModules[moduleIdx].position = _position
    });
  }

  onTitlePositionChange(position: {x: number; y: number}, item: ModuleTitleItem): void {
    const _position = position.x + ',' + position.y;
    this.mapperService.createOrUpdateTitle(this.plan, {
      ...item,
      position: _position
    }).subscribe(() => {
      const titleIdx = this.listTitles.findIndex(i => i.id === item.id);
      this.listTitles[titleIdx].position = _position;
    });
  }

  onZoomPositionChange(position: {x: number; y: number}, item: Snapshot): void {
    const _position = position.x + ',' + position.y;
    this.mapperService.createOrUpdateSnapshot(this.plan, {
      ...item,
      type: item.id_zoom as SnapshotType,
      position: _position
    }).subscribe(() => {
      const zoomIdx = this.listZoom.findIndex(i => i.id_zoom === item.id_zoom);
      this.listZoom[zoomIdx].position = _position;
    });
  }
}
